import { useState, useMemo } from "react";
import { useGameStore, type TargetMode } from "@/store/gameStore";
import { useLegalActions } from "@/hooks/useLegalActions";
import { requiredDiscardCount } from "@/engine/LegalActions";
import type { EngineAction } from "@/engine/GameEngine";
import { findSubstituteConfig } from "@/engine/GameEngine";
import type { DeckCard } from "@/types/cards";
import type { PlayerState } from "@/types/game";
import Card from "./Card";

type PlayCardAction = Extract<EngineAction, { type: "play-card" }>;

// 通过 card.kind 推断 targetMode
function targetModeForCardKind(kind: DeckCard["kind"]): TargetMode {
  switch (kind) {
    case "攻击牌":
      return "attack";
    case "法术牌":
      return "spell";
    case "补给牌":
      return "supply";
    default:
      return "effect";
  }
}

// 获取法术牌可用的技能名列表
function skillNamesForCard(
  actions: PlayCardAction[] | undefined
): string[] {
  if (!actions) return [];
  return Array.from(
    new Set(actions.map((a) => a.skillName ?? "").filter(Boolean))
  );
}

// 技能描述 tooltip 文本
function skillTooltip(
  player: PlayerState | undefined,
  names: string[]
): string {
  if (!player || names.length === 0) return "";
  return names
    .map((n) => {
      const sk = player.hero.skills.find((s) => s.name === n);
      const cost = sk?.manaCost ? ` 耗魔:${sk.manaCost}` : "";
      return `${n}${cost}: ${sk?.description ?? ""}`;
    })
    .join("\n");
}

export default function Hand() {
  const state = useGameStore((s) => s.state);
  const humanId = useGameStore((s) => s.humanPlayerId);
  const selectedCardUid = useGameStore((s) => s.selectedCardUid);
  const pendingDiscardUids = useGameStore((s) => s.pendingDiscardUids);
  const dispatch = useGameStore((s) => s.dispatch);
  const setSelectedCard = useGameStore((s) => s.setSelectedCard);
  const setTargetMode = useGameStore((s) => s.setTargetMode);
  const setPendingSkillName = useGameStore((s) => s.setPendingSkillName);
  const setPendingPairFirst = useGameStore((s) => s.setPendingPairFirst);
  const setPendingSlotTargetId = useGameStore((s) => s.setPendingSlotTargetId);
  const setPendingSuitTargetId = useGameStore((s) => s.setPendingSuitTargetId);
  const setPendingStealTargetId = useGameStore((s) => s.setPendingStealTargetId);
  const toggleDiscardUid = useGameStore((s) => s.toggleDiscardUid);
  const substitutePicks = useGameStore((s) => s.substitutePicks);
  const setSubstitutePicks = useGameStore((s) => s.setSubstitutePicks);
  const clearSubstitutePicks = useGameStore((s) => s.clearSubstitutePicks);

  const legalActions = useLegalActions();
  // 当前展开技能子菜单的卡牌 uid
  const [skillMenuUid, setSkillMenuUid] = useState<number | null>(null);
  // 当前展开拾遗选择菜单的卡牌 uid
  const [takeMenuUid, setTakeMenuUid] = useState<number | null>(null);
  // 替换出牌模式：选2张手牌当某牌打出（杀戮之戒/狂风项链/掠夺之甲）
  const [subMode, setSubMode] = useState(false);

  const human = state?.players.find((p) => p.id === humanId);

  // 按 cardUid 索引合法 play-card 动作（仅人类回合 play 阶段）
  const actionsByUid = useMemo(() => {
    const out = new Map<number, PlayCardAction[]>();
    if (!human || state?.phase !== "play" || state.currentPlayerIdx !== state.players.findIndex((p) => p.id === humanId)) {
      return out;
    }
    for (const a of legalActions) {
      if (a.type !== "play-card") continue;
      if (a.playerId !== humanId) continue;
      const list = out.get(a.cardUid) ?? [];
      list.push(a);
      out.set(a.cardUid, list);
    }
    return out;
  }, [legalActions, human, state, humanId]);

  // 替换出牌候选（cardUid=-1 的 play-card 动作）
  const substituteActions = useMemo(() => {
    if (!human || state?.phase !== "play" || state.currentPlayerIdx !== state.players.findIndex((p) => p.id === humanId)) {
      return [];
    }
    return legalActions.filter(
      (a): a is PlayCardAction => a.type === "play-card" && a.cardUid === -1 && a.playerId === humanId
    );
  }, [legalActions, human, state, humanId]);

  // 可出的 cardUid 集合（用于高亮）
  const playableUids = useMemo(() => new Set(actionsByUid.keys()), [actionsByUid]);

  if (!state || !human) return null;

  const isHumanTurn =
    state.phase === "play" &&
    state.players[state.currentPlayerIdx].id === humanId;
  const isMyPhase =
    state.players[state.currentPlayerIdx].id === humanId;
  const isDiscardPhase =
    state.phase === "discard" &&
    state.players[state.currentPlayerIdx].id === humanId;

  // 弃牌阶段：已达上限后剩余牌 disabled
  // 仅统计手牌中真实存在的已选 uid（防御跨阶段残留的旧 uid 导致未选就满额）
  const need = requiredDiscardCount(human);
  const selectedInHand = pendingDiscardUids.filter((uid) =>
    human.hand.some((c) => c.uid === uid)
  ).length;
  const discardFull = selectedInHand >= need && need > 0;

  // 替换出牌的花色限制（狂风刃甲需黑色）
  const subSuitFilter = useMemo(() => {
    if (!human) return undefined;
    const sub = findSubstituteConfig(human, "active");
    return sub?.config.suitFilter;
  }, [human]);

  // 替换出牌：选2张手牌
  function toggleSubPick(uid: number) {
    const prev = substitutePicks;
    const next = prev.includes(uid)
      ? prev.filter((x) => x !== uid)
      : prev.length < 2
        ? [...prev, uid]
        : [prev[1], uid];
    setSubstitutePicks(next);
  }

  // 替换出牌：确认2张手牌后进入目标选择
  function confirmSubstitute() {
    if (substitutePicks.length !== 2) return;
    const actions = substituteActions;
    if (actions.length === 0) return;
    const subAs = actions[0].substituteAs;
    if (subAs === "掠夺") {
      setSelectedCard(-1);
      setPendingSkillName(null);
      setPendingSlotTargetId(null);
      setTargetMode("slot");
    } else {
      setSelectedCard(-1);
      setPendingSkillName(null);
      setPendingPairFirst(null);
      setTargetMode("attack");
    }
    setSubMode(false); // 退出选牌模式，进入目标选择
  }

  function handleCardClick(card: DeckCard) {
    if (isDiscardPhase) {
      toggleDiscardUid(card.uid);
      return;
    }
    // 替换出牌模式：选2张手牌
    if (subMode && isHumanTurn) {
      toggleSubPick(card.uid);
      return;
    }
    if (!isHumanTurn) return;
    const list = actionsByUid.get(card.uid);
    if (!list || list.length === 0) return;

    // 拾遗类：从弃牌堆选一张取回
    const takeActions = list.filter((a) => a.takeFromDiscardUid !== undefined);
    if (takeActions.length > 0) {
      if (takeActions.length === 1) {
        dispatch(takeActions[0]);
      } else {
        setTakeMenuUid(card.uid);
      }
      return;
    }

    // 缴械/掠夺：先选敌方目标，再选具体装备槽（武器/防具/饰品）
    if (card.name === "缴械" || card.name === "掠夺") {
      setSelectedCard(card.uid);
      setPendingSkillName(null);
      setPendingSlotTargetId(null);
      setTargetMode("slot");
      return;
    }

    // 杀戮之心：先选敌方目标，再盲选花色（红色/黑色/蓝色）
    if (card.name === "杀戮之心") {
      setSelectedCard(card.uid);
      setPendingSkillName(null);
      setPendingSuitTargetId(null);
      setTargetMode("suit");
      return;
    }

    // 飞龙探云手：先选敌方目标，再从其背面手牌中点选一张抽走
    if (card.name === "飞龙探云手") {
      setSelectedCard(card.uid);
      setPendingSkillName(null);
      setPendingStealTargetId(null);
      setTargetMode("steal-card");
      return;
    }

    // 命运链接：有 targetId2 的动作 → 进入两步目标选择
    if (list.some((a) => a.targetId2)) {
      setSelectedCard(card.uid);
      setPendingSkillName(null);
      setPendingPairFirst(null);
      setTargetMode("pair");
      return;
    }

    // 唯一动作
    if (list.length === 1) {
      const a = list[0];
      if (a.targetId) {
        setSelectedCard(card.uid);
        setPendingSkillName(a.skillName ?? null);
        setTargetMode(targetModeForCardKind(card.kind));
      } else {
        dispatch(a);
      }
      return;
    }

    // 多动作：判断是否有多个 skillName
    const skillNames = Array.from(
      new Set(list.map((a) => a.skillName ?? "").filter(Boolean))
    );
    if (skillNames.length > 1) {
      setSkillMenuUid(card.uid);
      return;
    }
    // 多目标但单一技能：直接进入目标选择
    setSelectedCard(card.uid);
    setPendingSkillName(skillNames[0] ?? null);
    setTargetMode(targetModeForCardKind(card.kind));
  }

  function pickSkill(card: DeckCard, skillName: string) {
    const list = actionsByUid.get(card.uid) ?? [];
    const matched = list.filter((a) => (a.skillName ?? "") === skillName);
    setSkillMenuUid(null);
    if (matched.length === 0) return;
    if (matched.length === 1 && !matched[0].targetId) {
      dispatch(matched[0]);
      return;
    }
    setSelectedCard(card.uid);
    setPendingSkillName(skillName);
    setTargetMode(targetModeForCardKind(card.kind));
  }

  const selectedSkillMenuCard =
    skillMenuUid != null ? human.hand.find((c) => c.uid === skillMenuUid) : null;

  const skillOptions = selectedSkillMenuCard
    ? (actionsByUid.get(selectedSkillMenuCard.uid) ?? [])
        .map((a) => a.skillName ?? "")
        .filter((v, i, arr) => v && arr.indexOf(v) === i)
    : [];

  // 拾遗菜单：列出可取的弃牌堆牌
  const takeMenuCard =
    takeMenuUid != null ? human.hand.find((c) => c.uid === takeMenuUid) : null;
  const takeOptions = takeMenuCard
    ? (actionsByUid.get(takeMenuCard.uid) ?? []).filter(
        (a) => a.takeFromDiscardUid !== undefined
      )
    : [];

  return (
    <div className={`hand${isDiscardPhase ? " hand--discard" : ""}`}>
      {isMyPhase && state.phase === "turn-end" && (
        <div className="hand__hint hand__hint--warn">
          点击下方「开始回合」开始你的回合
        </div>
      )}
      {isMyPhase && state.phase === "draw" && (
        <div className="hand__hint hand__hint--warn">
          点击下方「摸牌」摸取手牌后才能出牌
        </div>
      )}
      {isHumanTurn && !subMode && (
        <div className="hand__hint">
          {playableUids.size > 0
            ? "点击高亮卡牌出牌 · 法术牌触发英雄技能 · 悬停查看描述"
            : "没有可出的牌 · 点击「结束回合」"}
        </div>
      )}
      {isHumanTurn && subMode && (
        <div className="hand__hint hand__hint--sub">
          替换出牌：选择2张手牌当{substituteActions[0]?.substituteAs}打出
          （已选 {substitutePicks.length}/2）
          {substitutePicks.length === 2 && (
            <button className="hand__sub-confirm" onClick={confirmSubstitute}>
              确认
            </button>
          )}
          <button
            className="hand__sub-cancel"
            onClick={() => { setSubMode(false); clearSubstitutePicks(); }}
          >
            取消
          </button>
        </div>
      )}
      {isHumanTurn && !subMode && substituteActions.length > 0 && (
        <button
          className="hand__sub-toggle"
          onClick={() => setSubMode(true)}
        >
          替换出牌：用2张手牌当{substituteActions[0].substituteAs}打出
        </button>
      )}
      {human.hand.length === 0 ? (
        <span className="hand__empty">无手牌</span>
      ) : (
        human.hand.map((c) => {
          const playable = playableUids.has(c.uid);
          const isSelectedCard =
            selectedCardUid === c.uid ||
            (isDiscardPhase && pendingDiscardUids.includes(c.uid)) ||
            (subMode && substitutePicks.includes(c.uid));
          const disabled =
            (subMode
              ? subSuitFilter ? c.suit !== subSuitFilter : false
              : !playable && isHumanTurn) ||
            (isDiscardPhase &&
              discardFull &&
              !pendingDiscardUids.includes(c.uid)) ||
            (!isHumanTurn && !isDiscardPhase);
          const skills = skillNamesForCard(actionsByUid.get(c.uid));
          return (
            <div key={c.uid} className="hand__card-wrap">
              <Card
                card={c}
                compact
                highlight={playable && isHumanTurn}
                selected={isSelectedCard}
                disabled={disabled}
                onClick={() => handleCardClick(c)}
              />
              {isHumanTurn && playable && skills.length > 0 && (
                <div
                  className="hand__skill-label"
                  title={skillTooltip(human, skills)}
                >
                  {skills.join(" / ")}
                </div>
              )}
            </div>
          );
        })
      )}
      {selectedSkillMenuCard && skillOptions.length > 0 && (
        <div className="hand__skill-menu" role="dialog">
          <div className="hand__skill-menu-title">
            选择技能 · {selectedSkillMenuCard.name}
          </div>
          {skillOptions.map((name) => {
            const sk = human.hero.skills.find((s) => s.name === name);
            const cost = sk?.manaCost ? `（耗魔${sk.manaCost}）` : "";
            return (
              <button
                key={name}
                className="hand__skill-btn"
                title={sk?.description ?? ""}
                onClick={() => pickSkill(selectedSkillMenuCard, name)}
              >
                {name}
                {cost}
              </button>
            );
          })}
          <button
            className="hand__skill-cancel"
            onClick={() => setSkillMenuUid(null)}
          >
            取消
          </button>
        </div>
      )}
      {takeMenuCard && takeOptions.length > 0 && (
        <div className="hand__skill-menu" role="dialog">
          <div className="hand__skill-menu-title">
            拾遗 · 选择弃牌堆中的牌
          </div>
          {takeOptions.map((a) => {
            const c = state.discardPile.find(
              (x) => x.uid === a.takeFromDiscardUid
            );
            if (!c) return null;
            return (
              <button
                key={a.takeFromDiscardUid}
                className="hand__skill-btn"
                title={c.effectText ?? ""}
                onClick={() => {
                  setTakeMenuUid(null);
                  dispatch(a);
                }}
              >
                {c.name}
              </button>
            );
          })}
          <button
            className="hand__skill-cancel"
            onClick={() => setTakeMenuUid(null)}
          >
            取消
          </button>
        </div>
      )}
    </div>
  );
}
